/**
 * Accueil du Curieux (rôle `player`) — fonctions PURES, testées.
 *
 * Un Curieux n'a pas de niveau scolaire en base : son « niveau de révision »
 * vient de son dernier bilan (`/evaluer`, migration 0328), sinon du niveau
 * choisi dans l'app (onboarding, Feed « Live School », accueil), mémorisé sur
 * l'appareil. Il sert à proposer des exercices et à préfiltrer JeJoue.
 */

import { SECONDARY_SCHOOL_LEVELS, type SchoolLevel } from '@jeraisonne/db';

/** Même clé que le niveau Live School du Feed : un seul niveau pour tout l'espace. */
export const PLAYER_LEVEL_STORAGE_KEY = 'player_live_level';

export function isPlayerLevel(v: unknown): v is SchoolLevel {
  return SECONDARY_SCHOOL_LEVELS.some((l) => l.value === v);
}

export function readStoredPlayerLevel(): SchoolLevel | null {
  try {
    const v = window.localStorage.getItem(PLAYER_LEVEL_STORAGE_KEY);
    return isPlayerLevel(v) ? v : null;
  } catch {
    return null;
  }
}

export function storePlayerLevel(level: SchoolLevel): void {
  try {
    window.localStorage.setItem(PLAYER_LEVEL_STORAGE_KEY, level);
  } catch {
    // stockage indisponible (mode privé) : le choix reste en mémoire de session.
  }
}

/**
 * Niveau que révise le Curieux : le niveau choisi explicitement sur l'appareil
 * l'emporte (il vient de le choisir), sinon celui du bilan en cours ou du
 * dernier bilan. `null` = inconnu (on lui proposera d'en choisir un).
 */
export function revisionLevel(input: {
  stored: string | null | undefined;
  inProgressLevel?: string | null;
  lastCompletedLevel?: string | null;
}): SchoolLevel | null {
  for (const v of [input.stored, input.inProgressLevel, input.lastCompletedLevel]) {
    if (isPlayerLevel(v)) return v;
  }
  return null;
}

/** « Sophie Bernard » → « Sophie » (accueil plus court, sans retour à la ligne). */
export function firstName(displayName: string | null | undefined): string {
  return (displayName ?? '').trim().split(/\s+/)[0] ?? '';
}

export interface PlayerExerciseLike {
  id: string;
  chapters: { level: string } | null;
}

/**
 * Exercices à proposer : ceux du niveau de révision pas encore faits, dans
 * l'ordre reçu (les plus récents d'abord). Sans niveau : les premiers pas faits.
 */
export function suggestExercises<T extends PlayerExerciseLike>(
  exercises: readonly T[],
  doneIds: ReadonlySet<string>,
  level: string | null,
  limit = 3,
): T[] {
  return exercises
    .filter((e) => !doneIds.has(e.id) && (!level || e.chapters?.level === level))
    .slice(0, limit);
}

export interface PlayerAttemptLike {
  exercise_id: string;
  completed_at: string | null;
}

/** Nombre d'exercices DIFFÉRENTS terminés. */
export function distinctCompleted(attempts: readonly PlayerAttemptLike[]): number {
  return new Set(attempts.map((a) => a.exercise_id)).size;
}

/** Dernière tentative terminée (la plus récente), ou null. */
export function lastAttempt<T extends PlayerAttemptLike>(attempts: readonly T[]): T | null {
  let best: T | null = null;
  for (const a of attempts) {
    if (!a.completed_at) continue;
    if (!best || (best.completed_at ?? '') < a.completed_at) best = a;
  }
  return best;
}
